import React, { useRef, useEffect, useCallback } from 'react';
import clsx from 'clsx';
import { useDispatch, useSelector } from 'react-redux';
import { makeStyles } from '@mui/styles';
import _ from 'underscore';
import { Button, Card, CardHeader, CardContent, CardActions, Grid, Divider, colors } from '@mui/material';
import { FilePicker, Checkbox } from 'components';
import validate from 'validate.js';

import TextField from '@mui/material/TextField';
import Autocomplete from '@mui/material/Autocomplete';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import CheckBoxIcon from '@mui/icons-material/CheckBox';

import { enqueueSnackbar } from 'actions';
import { SetFormState, GenerateScf } from '../../../actions/ScfActions.js';

const icon = <CheckBoxOutlineBlankIcon fontSize="small" />;
const checkedIcon = <CheckBoxIcon fontSize="small" />;

const useStyles = makeStyles((theme) => ({
	root: {
		padding: theme.spacing(3),
	},
	container: {
		marginTop: theme.spacing(3),
	},
	tabs: {
		marginTop: theme.spacing(3),
	},
	content: {
		marginTop: theme.spacing(3),
	},
	generate: {
		color: theme.palette.white,
		backgroundColor: colors.green[600],
		'&:hover': {
			backgroundColor: colors.green[900],
		},
	},
}));

const schema = {
	scfInput: {
		presence: { allowEmpty: false, message: 'is required' },
	},
	selectedSowType: {
		presence: { allowEmpty: false, message: 'is required' },
	},
	selectedSowTechs: {
		presence: { allowEmpty: false, message: 'is required' },
	},
};

const ScfForm = () => {
	const classes = useStyles();
	const dispatch = useDispatch();
	const setFormState = formState => dispatch(SetFormState(formState));
	const formState = useSelector((state) => state.scfReducer.formState);
	const sowType = useSelector((state) => state.scfReducer.sowType);
	const sowTechs = useSelector((state) => state.scfReducer.sowTechs);
	const sowRmod = useSelector((state) => state.scfReducer.sowRmod);

	useEffect(() => {
		const errors = validate(formState.values, schema);
		setFormState({
			...formState,
			isValid: !errors,
			errors: errors || {},
		});
	}, [formState.values]);

	const _onChangeInput = (event) => {
		if (event.target.files && event.target.files.length > 0) {
			const file = event.target.files[0];
			setFormState({
				...formState,
				values: {
					...formState.values,
					scfInput: file,
				},
				touched: {
					...formState.touched,
					scfInput: true,
				},
			});
		}
	};

	const _handleChange = (event) => {
		setFormState({
			...formState,
			values: {
				...formState.values,
				[event.target.name]:
					event.target.type === 'checkbox'
						? event.target.checked
						: event.target.value,
			},
			touched: {
				...formState.touched,
				[event.target.name]: true,
			},
		});
	};

	const _handleCheckboxChange = (event) => {
		const { name, checked } = event.target;
		setFormState({
			...formState,
			values: {
				...formState.values,
				[name]: checked,
				sran: checked,
			},
			touched: {
				...formState.touched,
				[name]: true,
				sran: true,
			},
		});
	};

	const _handleChangeSowType = (event, value) => {
		setFormState({
			...formState,
			values: {
				...formState.values,
				selectedSowType: value?.value
			},
			touched: {
				...formState.touched,
				[event.target.name]: true,
			},
		});
	};

	const _handleChangeSowTechs = (event, value) => {
		setFormState({
			...formState,
			values: {
				...formState.values,
				// eslint-disable-next-line max-len
				selectedSowTechs: value.map(u => (u.value === '3500C2' || u.value === 'L800' || u.value === 'L2100' || u.value === '2100 DSS' || u.value === '1800 DSS' ? u.value : Number(u.value))).sort((a, b) => a - b).join('+')
			},
			touched: {
				...formState.touched,
				[event.target.name]: true,
			},
		});
	};

	const _handleChangeSowRmod = (event, value) => {
		setFormState({
			...formState,
			values: {
				...formState.values,
				selectedSowRmod: value.map(u => u.value).sort((a, b) => (a > b ? 1 : -1)).join('+')
			},
			touched: {
				...formState.touched,
				[event.target.name]: true,
			},
		});
	};

	const _generate = () => {
		if (formState.values.scfInput.size > 100000000) {
			dispatch(enqueueSnackbar({ message: 'File size cannot be superior to 100MB', options: { variant: 'warning' } }));
			return;
		}
		dispatch(GenerateScf(
			formState.values.scfInput,
			formState.values.selectedSowType,
			formState.values.selectedSowTechs,
			formState.values.selectedSowRmod,
			formState.values.sran,
			formState.values.sa));
	};

	const groupNames = {
		'Tech Add': 'Tech Add',
		'New Buil': 'New Build'
	};

	const doesNotIncludePlus = x => !x?.value.includes('+');
	const isNotEmpty = x => x?.value !== '';

	const doesNotIncludeRadioNR700 = x => !x.value.includes('ARPA');
	const doesNotIncludeRadioL800 = x => !x.value.includes('ARMA') && !x.value.includes('FRMF');

	const doesNotIncludeRadioNR1800 = x => !x.value.includes('AREA');
	const doesNotIncludeRadioNR2100L2100 = x => !x.value.includes('ARGA');

	const doesNotIncludeRadioNR2600 = x => !x.value.includes('ARHA');

	const filterOptions = (options, selectedSowTechs) => options
		.filter(doesNotIncludePlus)
		.filter(isNotEmpty)
		.filter(x => (!selectedSowTechs?.includes('700') ? doesNotIncludeRadioNR700(x) : x))
		.filter(x => (!selectedSowTechs?.includes('L800') ? doesNotIncludeRadioL800(x) : x))
		.filter(x => (!selectedSowTechs?.includes('1800') ? doesNotIncludeRadioNR1800(x) : x))
		.filter(x => (!selectedSowTechs?.includes('2100') ? doesNotIncludeRadioNR2100L2100(x) : x))
		.filter(x => (!selectedSowTechs?.includes('2600') ? doesNotIncludeRadioNR2600(x) : x));

	return (
		<Card className={classes.content}>
			<CardHeader title="Nokia SCF" />
			<Divider />
			<CardContent>
				<div style={{ padding: 10, paddingBottom: 30 }}>
					<Grid item md={3} xs={12}>
						<FilePicker value={formState.values.scfInput?.name} onChange={_onChangeInput} accept=".zip" />
					</Grid>
				</div>
				<div style={{ padding: 10 }}>
					<Grid item md={3} xs={12}>
						<Autocomplete
							options={sowType.filter(x => !x?.value.includes('Legacy'))}
							id="selectedSowType"
							name="selectedSowType"
							groupBy={(option) => {
								const groupName = option.value.slice(0, 8);
								return groupNames[groupName] || 'Conversions';
							}}
							onChange={_handleChangeSowType}
							getOptionLabel={(option) => option.value}
							renderOption={(props, option) => (
								<li {...props}>
									{option.value}
								</li>
							)}
							renderInput={(params) => (<TextField {...params} label="Select Type" placeholder="" />)}
						/>
					</Grid>
				</div>
				<div style={{ padding: 10 }}>
					<Grid item md={3} xs={12}>
						<Autocomplete
							multiple
							options={sowTechs}
							id="selectedSowTechs"
							name="selectedSowTechs"
							disableCloseOnSelect
							onChange={_handleChangeSowTechs}
							getOptionLabel={(option) => option.value}
							renderOption={(props, option, { selected }) => (
								<li {...props}>
									<Checkbox
										icon={icon}
										checkedIcon={checkedIcon}
										checked={(selected || _.findWhere(formState.values.selectedSowTechs, option) !== undefined)}
									/>
									{option.value}
								</li>
							)}
							renderInput={(params) => (<TextField {...params} label="Select Tech(s)" placeholder="" />)}
						/>
					</Grid>
				</div>
				<div style={{ padding: 10 }}>
					<Grid item md={3} xs={12}>
						<Autocomplete
							multiple
							 
							options={filterOptions(sowRmod, formState.values.selectedSowTechs)}
							id="selectedSowRmod"
							name="selectedSowRmod"
							disableCloseOnSelect
							onChange={_handleChangeSowRmod}
							getOptionLabel={(option) => option.value}
							renderOption={(props, option, { selected }) => (
								<li {...props}>
									<Checkbox
										icon={icon}
										checkedIcon={checkedIcon}
										style={{ marginRight: 8 }}
										checked={(selected || _.findWhere(formState.values.selectedSowRmod, option) !== undefined)}
									/>
									{option.value}
								</li>
							)}
							renderInput={(params) => (<TextField {...params} label="Select RMOD" placeholder="" />)}
						/>
					</Grid>
				</div>
				<div style={{ padding: 10 }}>
					<Grid container item md={3} xs={12} direction="row" justifyContent="space-around">
						<Checkbox id="sran" key="sran" name="sran" checked={formState.values.sran} label="SRAN" onCheck={_handleCheckboxChange} />
						<Checkbox id="sa" key="sa" name="sa" checked={formState.values.sa} label="SA" onCheck={_handleCheckboxChange} />
					</Grid>
				</div>
			</CardContent>
			<Divider />
			<CardActions>
				<Button
					className={classes.generate}
					onClick={_generate}
					variant="contained"
					// eslint-disable-next-line max-len
					disabled={!formState.isValid && formState.values?.selectedSowType !== 'Convert to SRAN ' && formState.values?.selectedSowType !== 'Convert to Standalone ' && formState.values?.selectedSowType !== 'Convert to 20MHz '}
				>
					Generate
				</Button>
			</CardActions>
		</Card>
	);
};

export default ScfForm;
